import React from 'react'
import SmallTitle from '../atoms/MediumTitle'
import Paragraph from '../atoms/Paragraph'
import { groupBlogElements } from '../utils/groupBlogElements'
import Li from '../atoms/Li'
import {
  PrivacyStatement_Content_List,
  PrivacyStatement_Content_Paragraph,
  PrivacyStatement_Content_TightParagraph,
  PrivacyStatement_Content_Title,
  PrivacyStatement,
  PrivacyStatement_Content_RelaxedList,
  PrivacyStatement_Content_OrderedList,
  PrivacyStatement_Content_BreakLine,
} from '@/content/types'
import Prose from '../atoms/Prose'
import ReactMarkdown from 'react-markdown'
import ColorLink from '@/atoms/ColorLink'

type Content = PrivacyStatement['content']
type ContentItem = Content[number]

type Props = {
  content: Content
}

const parComponents = {
  p: ({ children }) => (
    <Paragraph className="text-sm leading-8 mb-8">{children}</Paragraph>
  ),
  a: ColorLink,
}

const tightParComponents = {
  p: ({ children }) => (
    <Paragraph className="text-sm leading-8 mb-0">{children}</Paragraph>
  ),
  a: ColorLink,
}

const listComponents = {
  p: ({ children }) => <Li>{children}</Li>,
  a: ColorLink,
}

const relaxedListComponents = {
  p: ({ children }) => (
    <li className="mb-4 leading-8 marker:text-blue marker:text-xl pl-2">
      {children}
    </li>
  ),
  a: ColorLink,
  ul: ({ children }) => <ul className="list-[circle] ml-8">{children}</ul>,
  li: ({ children }) => (
    <li className="marker:text-blue marker:text-xl pl-2">{children}</li>
  ),
}

const orderedListComponents = {
  p: ({ children }) => (
    <li className="leading-8 marker:text-blue pl-4">{children}</li>
  ),
  a: ColorLink,
}

export const renderItem = (item: ContentItem, i: number) => {
  switch (item.type) {
    case 'paragraph': {
      const par = item as PrivacyStatement_Content_Paragraph
      return (
        <ReactMarkdown key={i} components={parComponents}>
          {par.content}
        </ReactMarkdown>
      )
    }

    case 'tight_paragraph': {
      const tight_par = item as PrivacyStatement_Content_TightParagraph
      return (
        <ReactMarkdown key={i} components={tightParComponents}>
          {tight_par.content}
        </ReactMarkdown>
      )
    }

    case 'title': {
      const title = item as PrivacyStatement_Content_Title
      return <SmallTitle key={i}>{title.content}</SmallTitle>
    }

    case 'list': {
      const list = item as PrivacyStatement_Content_List
      return (
        <div key={i} className="text-sm mb-8">
          <ul>
            {list.items.map((item, i) => (
              <ReactMarkdown key={i} components={listComponents}>
                {item.item}
              </ReactMarkdown>
            ))}
          </ul>
        </div>
      )
    }

    case 'break_line': {
      const _breakLine = item as PrivacyStatement_Content_BreakLine
      return <br key={i} />
    }

    case 'relaxed_list': {
      const list = item as PrivacyStatement_Content_RelaxedList
      return (
        <div key={i} className="text-sm mb-8">
          <ul className="list-disc ml-6">
            {list.items.map((item, i) => (
              <ReactMarkdown key={i} components={relaxedListComponents}>
                {item.item}
              </ReactMarkdown>
            ))}
          </ul>
        </div>
      )
    }

    case 'ordered_list': {
      const list = item as PrivacyStatement_Content_OrderedList
      return (
        <div key={i} className="text-sm mb-8">
          <ol className="list-decimal ml-5">
            {list.items.map((item, i) => (
              <ReactMarkdown key={i} components={orderedListComponents}>
                {item.item}
              </ReactMarkdown>
            ))}
          </ol>
        </div>
      )
    }
  }
}

const LegalContent = ({ content }: Props) => {
  const groups = groupBlogElements(content)

  return (
    <div className="py-8">
      {groups.map((g, i) => (
        <div key={i}>
          <Prose>{g.map(renderItem)}</Prose>
        </div>
      ))}
    </div>
  )
}

export default LegalContent
